import {
    type DynamicListItem,
    type HTMLDynamicListElement,
    ListUpdatedEvent,
} from "@modules/_components/HTMLDynamicListElement"
import type { HTMLTagsListElement } from "@modules/bookmarks/entries/entry-cards"
import { requireElement } from "@modules/bookmarks/html-elements"
import { htmlToElement } from "@modules/bookmarks/html-parser"
import {
    TagAddSuccessEvent,
    TagDeleteSuccessEvent,
    TagRenameSuccessEvent,
    TagShowNewEvent,
    type TagsListData,
    tagList,
    tagsList,
} from "@modules/bookmarks/tags/tag-list"
import { applyTagToUserEntry, removeTagFromUserEntry } from "@modules/bookmarks/tags/tags-api"
import { tagAssignTagsBoxListItem } from "@modules/bookmarks/tags/tags-list-item"
import type { ApiError } from "@modules/global/ApiError"
import { InfoPanel } from "@modules/info-panel/InfoPanel"
import { type FloatingSidebarDetail, FloatingSidebarEvent } from "@modules/single-page/sticky-sidebar"
import { registerGlobalEventListener } from "@modules/util/events"

if (SiteInfo.bookmarksPageActive) {
    import("./tags-dropdown.css")
}

type TagData = DynamicListItem & TagsListData
type ExtendedTagData = TagData & { activeClass: string }
type TargetPseudoEvent = { currentTarget: HTMLElement }

const newTagButton: string = `<footer>
        <lds-button size="sm" variant="secondary">
            <svg viewBox="0 0 20 20"><use href="#plus-icon"></use></svg>
            <span>Neues Tag anlegen</span>
        </lds-button>
    </footer>`

const makeTagListContainer = (): HTMLDynamicListElement<TagData> =>
    htmlToElement<HTMLDynamicListElement<TagData>>(`
        <dynamic-list class="card-tags-dropdown" list-container-type="div" container-class="tag-assign-tags-box">
            <template>${tagAssignTagsBoxListItem}</template>
            ${newTagButton}
        </dynamic-list>
    `)

let tagListContainerElement: HTMLDynamicListElement<TagData> | null = null
let tagListUpdatedCallback: ((newTagData: TagsListData) => Promise<void>) | null = null

export const registerTagListUpdatedCallback = (
    callback: ((newTagData: TagsListData) => Promise<void>) | null,
): void => {
    tagListUpdatedCallback = callback
}

const getTagClickHandler = (dataElement: HTMLTagsListElement | null, data: DynamicListItem & TagsListData) => {
    return async (clickEvent: PointerEvent | TargetPseudoEvent): Promise<void> => {
        const tagElement = clickEvent.currentTarget as HTMLElement

        if (!dataElement || !dataElement.dataset.advertId) {
            return
        }

        tagElement.classList.add("working")

        try {
            const tagsList = dataElement.tagsList || []

            if (tagElement.classList.contains("active")) {
                await removeTagFromUserEntry(
                    userData.userId,
                    Number.parseInt(dataElement.dataset.advertId),
                    dataElement.dataset.canonicalId || "",
                    data.tagSlug,
                )

                tagElement.classList.remove("active")
                dataElement.tagsList = tagsList.filter((tag: TagsListData) => tag.tagSlug !== data.tagSlug)
            } else {
                if (tagListUpdatedCallback !== null) {
                    await tagListUpdatedCallback(data)
                    await applyTagToUserEntry(
                        userData.userId,
                        Number.parseInt(dataElement.dataset.advertId),
                        dataElement.dataset.canonicalId || "",
                        data.tagSlug,
                    )
                } else {
                    await applyTagToUserEntry(
                        userData.userId,
                        Number.parseInt(dataElement.dataset.advertId),
                        dataElement.dataset.canonicalId || "",
                        data.tagSlug,
                    )
                }

                tagElement.classList.add("active")
                dataElement.tagsList = [...tagsList, { tagName: data.tagName, tagSlug: data.tagSlug }]
            }
        } catch (error) {
            InfoPanel.displayMessage((error as Error | ApiError).message)
        }

        tagElement.classList.remove("working")
    }
}

const updateTagListData = (newTagData?: TagsListData): void => {
    if (tagListContainerElement === null) {
        return
    }

    const dataElement = tagListContainerElement.closest<HTMLTagsListElement>(".entry-card, #einzelansicht")
    const dataTags = dataElement?.tagsList || []
    const dataTagSlugList = dataTags.map((tag: TagData): string => tag.tagSlug)

    const dataList: TagData[] = tagsList.filter((tagData: TagData): boolean => tagData.tagSlug !== "-")

    if (newTagData !== undefined) {
        tagListContainerElement.addEventListener(
            ListUpdatedEvent,
            (listEvent): void => {
                const currentTarget = listEvent.detail.items.find((tag) => tag.dataset.tagId === newTagData.tagSlug)

                if (!currentTarget) {
                    return
                }

                if (tagListUpdatedCallback !== null) {
                    tagListUpdatedCallback(newTagData).then(() =>
                        getTagClickHandler(dataElement, newTagData)({ currentTarget }),
                    )
                } else {
                    getTagClickHandler(dataElement, newTagData)({ currentTarget })
                }
            },
            { once: true, passive: true },
        )
    }

    tagListContainerElement.dataList = dataList.map(
        (data: TagData): ExtendedTagData =>
            ({
                ...data,
                activeClass: dataTagSlugList.indexOf(data.tagSlug) >= 0 ? " active" : "",
                events: {
                    click: getTagClickHandler(dataElement, data),
                },
            }) as ExtendedTagData,
    )
}

export const openTagDropdownForCard = (card: HTMLArticleElement): void => {
    if (tagListContainerElement === null) {
        tagListContainerElement = makeTagListContainer()

        requireElement("lds-button", tagListContainerElement)?.addEventListener(
            "click",
            (): void => {
                tagList?.dispatchEvent(new CustomEvent(TagShowNewEvent, { bubbles: true }))
            },
            { passive: true },
        )
    }

    const entryCardTextContentsContainer: HTMLElement | null =
        card.querySelector<HTMLElement>(".entry-card--text-contents")
    if (entryCardTextContentsContainer === null) {
        return
    }

    if (
        tagListContainerElement.parentElement === entryCardTextContentsContainer &&
        !tagListContainerElement.hasAttribute("hidden")
    ) {
        tagListContainerElement.setAttribute("hidden", "true")
    } else {
        entryCardTextContentsContainer?.appendChild(tagListContainerElement)
        tagListContainerElement.removeAttribute("hidden")
    }

    updateTagListData()
}

export const openTagDropdownForSinglePage = (panel: HTMLElement): void => {
    if (tagListContainerElement === null) {
        tagListContainerElement = makeTagListContainer()

        requireElement("lds-button", tagListContainerElement)?.addEventListener(
            "click",
            (): void => {
                document.dispatchEvent(new CustomEvent(TagShowNewEvent, { bubbles: true }))
            },
            { passive: true },
        )
    }

    if (tagListContainerElement.parentElement === panel && !tagListContainerElement.hasAttribute("hidden")) {
        tagListContainerElement.setAttribute("hidden", "true")
    } else {
        panel?.appendChild(tagListContainerElement)
        tagListContainerElement.removeAttribute("hidden")
    }

    updateTagListData()
}

function reparentTagsDropdownForStickySidebar(sidebarEvent: CustomEvent<FloatingSidebarDetail>): void {
    if (!tagListContainerElement || (tagListContainerElement.hasAttribute("hidden") ?? true)) {
        return
    }

    if (sidebarEvent.detail.isVisible()) {
        document.querySelector(".navbar-nav li.fake-merken")?.appendChild(tagListContainerElement)
    } else {
        document.getElementById("contacts-info-container")?.appendChild(tagListContainerElement)
    }
}

setTimeout(async () => {
    document.addEventListener(
        TagAddSuccessEvent,
        (tagEvent): void => {
            updateTagListData(tagEvent.detail)
        },
        { passive: true },
    )

    document.addEventListener(
        TagDeleteSuccessEvent,
        (): void => {
            updateTagListData()
        },
        { passive: true },
    )

    document.addEventListener(
        TagRenameSuccessEvent,
        (): void => {
            updateTagListData()
        },
        { passive: true },
    )

    document.addEventListener(
        "click",
        (clickEvent: PointerEvent): void => {
            if ((clickEvent.target as HTMLElement).closest(".merkzettel-tag-button, .card-tags-dropdown, .modal")) {
                return
            }

            tagListContainerElement?.setAttribute("hidden", "true")
        },
        { passive: true },
    )

    registerGlobalEventListener(
        FloatingSidebarEvent,
        (sidebarEvent): void => reparentTagsDropdownForStickySidebar(sidebarEvent),
        { passive: true },
    )
}, 1)
